import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, MessagesSquare, Gift, Gamepad2, Target, LifeBuoy, Heart, Rocket, ShieldCheck, Coins, Users } from "lucide-react";
import { useUi } from "@/lib/uiConfig";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { WhatsAppIcon } from "@/components/whatsapp/WhatsAppIcon";
import { trackWa } from "@/components/whatsapp/WhatsAppNewbieHint";

const BENEFITS = [
  [MessagesSquare, "Gemeinsam austauschen", "Lerne andere Mitglieder unserer itzPlay Community kennen, unterhalte dich mit ihnen und tausche Erfahrungen aus.", "from-emerald-400 to-green-600", "talk"],
  [Gift, "Gemeinsam Punkte sammeln", "Entdeckt zusammen Möglichkeiten, Punkte zu sammeln, gebt euch gegenseitig Tipps und motiviert euch bei euren Community-Aktivitäten.", "from-amber-300 to-orange-500", "points"],
  [Gamepad2, "Gemeinsam spielen", "Tauscht euch über Spiele und Aktionen unserer Community aus und habt gemeinsam Spaß beim Spielen.", "from-pink-400 to-rose-600", "play"],
  [Target, "Aufgaben gemeinsam lösen", "Unterstützt euch gegenseitig bei Community-Aufgaben, Tagesmissionen und Herausforderungen.", "from-fuchsia-400 to-pink-600", "tasks"],
  [LifeBuoy, "Hilfe & Support erhalten", "Du hast Fragen zur Plattform, zum Punktesystem, zu Prämien oder zu technischen Funktionen? Unsere Gruppe bietet einen zusätzlichen Ort, an dem Mitglieder und das Community-Team helfen können.", "from-sky-400 to-emerald-500", "help"],
  [Heart, "Teil einer Gemeinschaft sein", "Erlebe unsere Community nicht nur als soziales Netzwerk, sondern als Gemeinschaft, in der man sich austauscht, zusammen lacht und gegenseitig unterstützt.", "from-rose-400 to-pink-600", "together"],
];

function useReveal() {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) { setShown(true); return undefined; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } }, { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, shown];
}

const Reveal = ({ children, delay = 0, className = "" }) => {
  const [ref, shown] = useReveal();
  return <div ref={ref} className={`wa-reveal ${shown ? "is-in" : ""} ${className}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
};

function JoinButton({ link, testId }) {
  if (!link) {
    return (
      <div className="space-y-2 text-center" data-testid={`${testId}-disabled`}>
        <button type="button" disabled className="w-full max-w-sm mx-auto h-14 rounded-full bg-slate-200 text-slate-500 font-extrabold flex items-center justify-center gap-2 cursor-not-allowed"><WhatsAppIcon className="w-6 h-6" /> Jetzt gemeinsam Punkte sammeln</button>
        <p className="text-xs text-slate-500">Der Einladungslink wird gerade vorbereitet – schau bald wieder vorbei.</p>
      </div>
    );
  }
  return (
    <a href={link} target="_blank" rel="noopener noreferrer" data-testid={testId} onClick={() => trackWa("join", testId.replace("wa-join-", ""))}
      className="tap wa-btn wa-glow relative w-full max-w-sm mx-auto h-14 px-6 rounded-full text-white font-extrabold text-base sm:text-lg flex items-center justify-center gap-2.5 overflow-hidden">
      <span className="wa-sheen" aria-hidden />
      <WhatsAppIcon className="relative w-7 h-7" /> <span className="relative">Jetzt gemeinsam Punkte sammeln</span>
    </a>
  );
}

const Bubble = ({ side = "left", children, className = "", delay = 0 }) => (
  <span className={`wa-bubble absolute px-3 py-2 rounded-2xl text-xs font-bold shadow-lg ${side === "left" ? "bg-white text-slate-700 rounded-bl-md" : "bg-[#dcf8c6] text-emerald-900 rounded-br-md"} ${className}`} style={{ animationDelay: `${delay}s` }}>{children}</span>
);

function HeroArt() {
  return (
    <div className="relative mx-auto w-full max-w-sm h-64 sm:h-72" aria-hidden data-testid="wa-hero-art">
      <span className="absolute inset-6 rounded-full bg-[radial-gradient(circle,rgba(37,211,102,.35),rgba(244,63,126,.12)_55%,transparent_72%)]" />
      <span className="wa-float absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-28 h-28 rounded-[2rem] wa-btn flex items-center justify-center shadow-2xl shadow-emerald-400/50 ring-4 ring-white">
        <WhatsAppIcon className="w-16 h-16 text-white" />
      </span>
      <Bubble className="left-0 top-6" delay={0}>Wer macht heute das Quiz? 🧠</Bubble>
      <Bubble side="right" className="right-0 top-16" delay={0.8}>Ich! +15 Punkte 🎉</Bubble>
      <Bubble className="left-2 bottom-10" delay={1.6}>Tipp: Glücksrad nicht vergessen 🎡</Bubble>
      <Bubble side="right" className="right-2 bottom-2" delay={2.4}>Danke für die Hilfe 💚</Bubble>
      <span className="wa-orbit absolute right-10 top-0 w-10 h-10 rounded-full bg-gradient-to-br from-amber-200 to-amber-500 flex items-center justify-center shadow-lg"><Coins className="w-5 h-5 text-amber-900" /></span>
      <span className="wa-orbit absolute left-8 top-[45%] w-9 h-9 rounded-xl pink-gradient flex items-center justify-center shadow-lg" style={{ animationDelay: "-2s" }}><Gift className="w-4 h-4 text-white" /></span>
      <span className="wa-orbit absolute right-16 bottom-16 w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-lg" style={{ animationDelay: "-4s" }}><Heart className="w-4 h-4 text-rose-500" fill="currentColor" /></span>
    </div>
  );
}

const Privacy = () => (
  <p className="max-w-md mx-auto flex items-start gap-2 text-xs text-slate-500 text-left" data-testid="wa-privacy">
    <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
    <span>Gut zu wissen: In WhatsApp-Gruppen sind deine Telefonnummer und ggf. dein Profilbild und Name für andere Gruppenmitglieder sichtbar. Die Teilnahme ist freiwillig und keine Voraussetzung, um die Community zu nutzen oder Punkte zu sammeln.</span>
  </p>
);

export default function WhatsAppCommunity() {
  const ui = useUi();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { key, search, state } = useLocation();
  useEffect(() => {
    trackWa("visit", search.includes("von=hinweis") ? "hint" : search.includes("gm=") ? "nudge" : state?.from === "feed" ? "feed" : "direct").then((r) => {
      if (r?.data?.bonus) { toast.success(`+${r.data.bonus} Punkte – danke fürs Vorbeischauen! 💚`); refreshUser(); }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const link = ui?.whatsapp_link || null;
  return (
    <div className="max-w-3xl mx-auto space-y-10 pb-16" data-testid="wa-page">
      <button type="button" onClick={() => (key !== "default" ? navigate(-1) : navigate("/feed"))} data-testid="wa-back"
        className="tap inline-flex items-center gap-2 h-10 pl-3 pr-4 rounded-full bg-white border border-pink-100 text-sm font-bold text-slate-700 hover:bg-rose-50"><ArrowLeft className="w-4 h-4 text-rose-500" /> Zurück zur Community</button>

      <section className="wa-hero relative overflow-hidden rounded-[2rem] p-6 sm:p-10 text-center" data-testid="wa-hero">
        <HeroArt />
        <h1 className="hero-plain mt-4 font-display text-3xl sm:text-5xl font-black tracking-tight text-slate-900">💚 Gemeinsam macht Punkte sammeln noch mehr Spaß!</h1>
        <p className="mt-3 text-base sm:text-lg font-bold text-emerald-700">Werde Teil unserer WhatsApp-Gruppe zur itzPlay Community!</p>
        <p className="mt-3 text-sm sm:text-base text-slate-600 max-w-xl mx-auto">Tausche dich mit anderen Mitgliedern aus, entdeckt gemeinsam neue Möglichkeiten, sammelt Punkte, löst Aufgaben, spielt zusammen und helft euch gegenseitig.</p>
        <p className="mt-2 text-sm sm:text-base text-slate-600 max-w-xl mx-auto">Und das Beste: Ihr könnt euch bei Fragen rund um unsere Community gegenseitig unterstützen und Hilfe erhalten!</p>
        <div className="mt-6 space-y-3"><JoinButton link={link} testId="wa-join-top" /><Privacy /></div>
      </section>

      <section className="space-y-4" data-testid="wa-benefits">
        <Reveal><h2 className="font-display text-2xl font-extrabold text-center">Das erwartet dich in der Gruppe</h2></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {BENEFITS.map(([Icon, title, text, grad, id], i) => (
            <Reveal key={id} delay={(i % 3) * 90}>
              <article className="group h-full lux-card rounded-3xl p-5 hover:-translate-y-1 hover:shadow-xl hover:shadow-emerald-100 transition-[transform,box-shadow] duration-300" data-testid={`wa-benefit-${id}`}>
                <span className={`wa-icon inline-flex w-12 h-12 rounded-2xl bg-gradient-to-br ${grad} text-white items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}><Icon className="w-6 h-6" /></span>
                <h3 className="mt-3 font-display text-lg font-extrabold text-slate-900">{title}</h3>
                <p className="mt-1 text-sm text-slate-600 leading-relaxed">{text}</p>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal><div className="pt-2"><JoinButton link={link} testId="wa-join-middle" /></div></Reveal>
      </section>

      <Reveal>
        <section className="relative overflow-hidden rounded-[2rem] lux-hero text-white p-6 sm:p-10" data-testid="wa-together">
          <span className="lux-hero-orb w-48 h-48 -top-16 -right-10" />
          <div className="relative flex -space-x-3 mb-5" aria-hidden>
            {["from-emerald-300 to-green-500", "from-amber-200 to-amber-500", "from-pink-200 to-rose-400", "from-sky-200 to-emerald-400", "from-fuchsia-200 to-pink-400"].map((g, i) => (
              <span key={g} className={`wa-float w-12 h-12 rounded-full bg-gradient-to-br ${g} ring-4 ring-white/40 flex items-center justify-center`} style={{ animationDelay: `${i * -0.7}s` }}><Users className="w-5 h-5 text-white" /></span>
            ))}
          </div>
          <h2 className="relative font-display text-2xl sm:text-3xl font-black text-white">🚀 Zusammen erreichen wir mehr!</h2>
          <p className="relative mt-3 text-sm sm:text-base text-white/95">Warum alleine Punkte sammeln, wenn es gemeinsam noch mehr Spaß macht?</p>
          <p className="relative mt-2 text-sm sm:text-base text-white/95">In unserer WhatsApp-Gruppe kannst du andere Mitglieder kennenlernen, Tipps austauschen, bei Aufgaben zusammenarbeiten und dich über die neuesten Entwicklungen unserer Community informieren.</p>
          <p className="relative mt-2 text-sm sm:text-base text-white/95">Ob du gerade erst angefangen hast oder schon fleißig Punkte sammelst – bei uns bist du willkommen!</p>
          <p className="relative mt-5 inline-flex flex-wrap items-center gap-2 px-4 py-2 rounded-2xl bg-white text-emerald-700 font-extrabold text-sm sm:text-base shadow-lg"><Rocket className="w-4 h-4 text-rose-500" /> 💚 Gemeinsam spielen. Gemeinsam lachen. Gemeinsam Punkte sammeln.</p>
        </section>
      </Reveal>

      <Reveal>
        <section className="wa-cta relative overflow-hidden rounded-[2rem] p-6 sm:p-10 text-center space-y-4" data-testid="wa-cta">
          <span className="wa-float mx-auto w-16 h-16 rounded-2xl wa-btn flex items-center justify-center shadow-xl shadow-emerald-400/40"><WhatsAppIcon className="w-9 h-9 text-white" /></span>
          <h2 className="font-display text-3xl font-black text-slate-900">💚 Bist du dabei?</h2>
          <p className="text-sm sm:text-base text-slate-600 max-w-md mx-auto">Werde jetzt Teil unserer WhatsApp-Gruppe und entdecke, wie viel Spaß unsere itzPlay Community gemeinsam machen kann!</p>
          <JoinButton link={link} testId="wa-join-bottom" />
          <Privacy />
          <Link to="/feed" data-testid="wa-back-bottom" className="inline-block text-sm font-bold text-rose-600 hover:underline">Zurück zum Feed</Link>
        </section>
      </Reveal>
    </div>
  );
}
